import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/** UI date format for separation submodule pickers and read-only dates (API stays ISO / YYYY-MM-DD). */
export const SEPARATION_DATE_DISPLAY_FORMAT = "DD/MM/YYYY";

export const SEPARATION_DATETIME_DISPLAY_FORMAT = "DD/MM/YYYY [at] HH:mm";

/** Inclusive UTC calendar-day difference: toYmd − fromYmd (same calendar date → 0). */
export function utcCalendarDaysBetween(
  fromYmdUtc: string,
  toYmdUtc: string
): number {
  const a = dayjs.utc(fromYmdUtc, "YYYY-MM-DD").startOf("day");
  const b = dayjs.utc(toYmdUtc, "YYYY-MM-DD").startOf("day");
  return b.diff(a, "day");
}

/** Build UTC calendar date from Ant Design DatePicker's dayjs (local wall date → UTC same Y-M-D). */
export function utcYmdFromPickerCurrent(current: dayjs.Dayjs): string {
  // Do not use dayjs.utc([y, m, d]): arrays fall through to `new Date(array)`, which stringifies
  // to "y,m,d" and parses inconsistently (wrong calendar day in many timezones).
  const ms = Date.UTC(
    current.year(),
    current.month(),
    current.date(),
  );
  return dayjs.utc(ms).format("YYYY-MM-DD");
}

export function formatExitDateDisplay(isoOrYmd: string | undefined): string {
  if (!isoOrYmd) return "—";
  const ymd = isoOrYmd.slice(0, 10);
  const d = dayjs.utc(ymd, "YYYY-MM-DD");
  return d.isValid() ? d.format(SEPARATION_DATE_DISPLAY_FORMAT) : isoOrYmd;
}

/** Full ISO timestamp for “submitted on” style display */
export function formatExitSubmittedAt(iso: string | undefined): string {
  if (!iso) return "—";
  const d = dayjs.utc(iso);
  return d.isValid()
    ? d.format(SEPARATION_DATETIME_DISPLAY_FORMAT)
    : iso;
}

export type ExitLwdValidationInput = {
  resignationDateYmd: string;
  lastWorkingDayYmd: string;
  minNoticeEnforced: boolean;
  minNoticeCalendarDays: number;
};

export function validateExitLastWorkingDay(
  input: ExitLwdValidationInput,
): { ok: true } | { ok: false; message: string } {
  const gap = utcCalendarDaysBetween(
    input.resignationDateYmd,
    input.lastWorkingDayYmd,
  );
  if (gap < 0) {
    return {
      ok: false,
      message: "Last working date cannot be before the resignation date.",
    };
  }
  if (input.minNoticeEnforced) {
    if (gap < input.minNoticeCalendarDays) {
      return {
        ok: false,
        message: `Last working date must be at least ${input.minNoticeCalendarDays} calendar days after the resignation date.`,
      };
    }
  }
  return { ok: true };
}
